const REFRESH_SAFETY_WINDOW_MS = 60 * 1000;
const REFRESH_COOLDOWN_MS = 30 * 1000;

let tokenFetcher = null;
let cachedToken = null;
let cachedTokenExpiresAt = 0;
let pendingRefreshPromise = null;
let lastRefreshAttemptAt = 0;

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    return JSON.parse(window.atob(paddedPayload));
  } catch (error) {
    return null;
  }
};

export const isAuth0AccessTokenExpired = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }

  return payload.exp * 1000 <= Date.now();
};

const isTokenNearExpiry = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const expiresAt = payload.exp * 1000;
  cachedTokenExpiresAt = expiresAt;
  return expiresAt - Date.now() <= REFRESH_SAFETY_WINDOW_MS;
};

const saveToken = (token) => {
  cachedToken = token;
  const payload = decodeJwtPayload(token);
  cachedTokenExpiresAt = payload && payload.exp ? payload.exp * 1000 : 0;
};

export const registerAuth0TokenFetcher = (fetcher) => {
  tokenFetcher = fetcher;
};

export const clearAuth0TokenFetcher = () => {
  tokenFetcher = null;
  cachedToken = null;
  cachedTokenExpiresAt = 0;
  pendingRefreshPromise = null;
  lastRefreshAttemptAt = 0;
};

export const setCachedAuth0AccessToken = (token) => {
  if (token) {
    saveToken(token);
  }
};

export const getFreshAuth0AccessToken = async () => {
  if (cachedToken && !isTokenNearExpiry(cachedToken)) {
    return cachedToken;
  }

  if (!tokenFetcher) {
    return cachedToken;
  }

  const now = Date.now();
  if (pendingRefreshPromise) {
    return pendingRefreshPromise;
  }

  // Only apply cooldown when token is near expiry but NOT yet expired.
  // If already expired, always attempt refresh immediately.
  const tokenAlreadyExpired = !cachedToken || isAuth0AccessTokenExpired(cachedToken);
  if (!tokenAlreadyExpired && now - lastRefreshAttemptAt < REFRESH_COOLDOWN_MS && cachedToken) {
    return cachedToken;
  }

  lastRefreshAttemptAt = now;
  pendingRefreshPromise = tokenFetcher()
    .then((token) => {
      if (token) {
        saveToken(token);
        lastRefreshAttemptAt = 0; // reset so next natural expiry triggers refresh immediately
      }

      if (token) {
        return token;
      }

      return cachedToken && !isAuth0AccessTokenExpired(cachedToken) ? cachedToken : null;
    })
    .catch(() => (cachedToken && !isAuth0AccessTokenExpired(cachedToken) ? cachedToken : null))
    .finally(() => {
      pendingRefreshPromise = null;
    });

  return pendingRefreshPromise;
};